import React from "react";
import _ from "lodash";
import { useEditor } from "@tldraw/tldraw";
import { gql, useLazyQuery } from "@apollo/client";
import SearchIcon from "pages/flyer/builder/drawer/components/Primitives/icons/SearchIcon";
import { useBrandKitStore } from "store/brandKit";
import { uploadBrandKitAsset } from "pages/flyer/builder/services/brandKitUploadService";
import { useImageUpload } from "pages/flyer/builder/drawer/contexts/ImageUploadContext";
import { SnackBarContext } from "components/snack-bar";
import Icon from "./Item";

const SEARCH_ICONS = gql`
  query SearchIcons($query: String!, $next_page: String) {
    searchIcons(query: $query, next_page: $next_page) {
      icons {
        id
        name
        thumbnail_url
        svg_url
        svg
        uploader {
          name
          username
        }
        tags
      }
      next_page
    }
  }
`;

const IconsComponent = ({ searchOnEnter = false }) => {
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [icons, setIcons] = React.useState([]);
  const [nextPage, setNextPage] = React.useState(null);
  const containerRef = React.useRef(null);

  const editor = useEditor();

  const [searchIcons, { loading: isFetching, error }] = useLazyQuery(SEARCH_ICONS, {
    onCompleted: (data) => {
      if (data?.searchIcons) {
        setIcons((prevIcons) => {
          const existingIds = new Set(prevIcons.map((icon) => icon.id));
          const newIcons = (data.searchIcons.icons || []).filter((icon) => !existingIds.has(icon.id));

          if (newIcons.length === 0 && !data.searchIcons.next_page) {
            setNextPage(null);
          }

          return [...prevIcons, ...newIcons];
        });

        setNextPage(data.searchIcons.next_page || null);
      }
    },
    onError: (error) => {
      console.error("Error fetching icons:", error);
    },
  });

  const [fromBrandKit, setFromBrandKit] = React.useState(false);

  // On mount: check for pending Brand Kit search, otherwise pre-populate
  // with a random seed term. The empty-state used to land on a blank
  // sidebar with no preview grid; that's a poor "what can I do here?"
  // moment. Seeding a random common keyword gives the user something to
  // look at immediately and reads as "try one of these" rather than
  // "search for me to show anything". They overwrite as soon as they
  // type. Pool is short and generic so each pick still feels plausible.
  React.useEffect(() => {
    const pending = window.__pendingIconSearch;
    if (pending) {
      delete window.__pendingIconSearch;
      setIcons([]);
      setNextPage(null);
      setQuery(pending);
      setSearch(pending);
      setFromBrandKit(true);
      searchIcons({ variables: { query: pending, next_page: null } });
    } else {
      const seeds = [
        "arrow",
        "star",
        "heart",
        "user",
        "home",
        "cart",
        "phone",
        "email",
        "calendar",
        "clock",
        "search",
        "camera",
        "music",
        "play",
        "lock",
        "share",
        "gift",
        "chat",
        "settings",
        "globe",
      ];
      const seed = seeds[Math.floor(Math.random() * seeds.length)];
      // Fire the seed search but DON'T mirror it into `query`/`search` —
      // we want the grid populated for the "look what's here" first
      // impression while the input stays empty so the user reads it
      // as "type to search" instead of "this thing has been searched
      // for you, clear it". Side-effect: infinite scroll won't trigger
      // for the seed batch (scroll handler short-circuits when `query`
      // is empty); the first page of ~20 icons is enough for the
      // discovery moment and the user gets full pagination as soon as
      // they type a real query.
      setIcons([]);
      setNextPage(null);
      searchIcons({ variables: { query: seed, next_page: null } });
    }
  }, []);

  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = React.useContext(SnackBarContext);

  // Save icon SVG to Brand Kit (create file from SVG content → upload to S3)
  const handleSaveToBrandKit = React.useCallback(
    async (svgContent, iconName) => {
      startUpload("Saving icon to Brand Kit...");
      try {
        const blob = new Blob([svgContent], { type: "image/svg+xml" });
        const file = new File([blob], `${iconName}.svg`, { type: "image/svg+xml" });
        await uploadBrandKitAsset(file, "icon", iconName);
        useBrandKitStore.getState().loadBrandKit();
      } catch (err) {
        console.error("Failed to save icon to Brand Kit:", err);
        runSnackBar({
          msg: "Failed to save icon. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [startUpload, endUpload, runSnackBar]
  );

  const fetchIcons = React.useCallback(
    (query, pageToken = null) => {
      if (!query || isFetching) return;

      searchIcons({
        variables: {
          query,
          next_page: pageToken,
        },
      });
    },
    [searchIcons, isFetching]
  );

  const handleScroll = () => {
    const container = containerRef.current;
    if (
      container &&
      container.scrollTop + container.clientHeight >= container.scrollHeight - 20 &&
      !isFetching &&
      nextPage
    ) {
      fetchIcons(query, nextPage);
    }
  };

  React.useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [query, nextPage, isFetching, fetchIcons]);

  const debouncedFetchIcons = _.debounce((query) => {
    setIcons([]);
    fetchIcons(query);
  }, 1000);

  const handleChange = (value) => {
    const newQuery = value.target.value;
    setQuery(newQuery);
    // Only search automatically if NOT searchOnEnter mode
    if (!searchOnEnter) {
      debouncedFetchIcons(newQuery);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const searchQuery = event.currentTarget.value.trim();
      if (searchQuery) {
        setIcons([]);
        setNextPage(null);
        setSearch(searchQuery);
        setQuery(searchQuery);
        fetchIcons(searchQuery);
      }
    }
  };

  // Reset search when icon is selected in edit mode
  const handleIconSelected = () => {
    if (searchOnEnter) {
      setQuery("");
      setSearch("");
      setIcons([]);
      setNextPage(null);
    }
  };

  return (
    <div className={`flex flex-col ${searchOnEnter && !search ? "" : "h-full"} pl-4 pr-4 pb-4 pt-2 bg-white w-[240px]`}>
      <div>
        <div className="relative mb-3">
          <div className="absolute top-1/2 -translate-y-1/2 left-1 ">
            <SearchIcon />
          </div>
          <input
            className="w-full h-[32px] border border-[#E2E2FC] rounded-[5px] pl-[25px] py-[3px] placeholder:[#707087] hover:shadow-custom transitino-all"
            placeholder="Search icons here"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={query}
          />
        </div>
      </div>
      {/* Only show the container of icons if there is a search performed or if searchOnEnter is not active */}
      {(!searchOnEnter || search) && (
        <div className="flex-1 overflow-hidden">
          <div className="flex-1 h-full">
            {error ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <div className="text-red-500 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">Error loading icons</p>
                {/* Don't leak raw backend error text (e.g. the
                    `Cannot read property 'role' of undefined` the
                    searchIcons resolver throws when called without a
                    user — that's a backend bug surfaced to anonymous
                    callers). Show a friendly generic message instead. */}
                <p className="text-xs text-gray-500 mt-1">Try a different search or check back later.</p>
              </div>
            ) : isFetching && icons.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Loading icons...</p>
                </div>
              </div>
            ) : icons.length === 0 && query ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <div className="text-gray-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">No icons found</p>
                <p className="text-xs text-gray-500 mt-1">Try searching with different keywords</p>
              </div>
            ) : icons.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <svg
                  className="w-16 h-16 mx-auto text-gray-300 mb-4"
                  fill="currentColor"
                  viewBox="-2.4 -2.4 28.80 28.80"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19,13a1,1,0,0,0-1,1v.39l-1.48-1.48a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.87,2.87,0,0,0-3.93,0L4,12.61V7A1,1,0,0,1,5,6H9A1,1,0,0,0,9,4H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.89a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.45,20Zm13-1a1,1,0,0,1-.18.54L13.31,15l.7-.69a.77.77,0,0,1,1.1,0L18,17.22Zm3.71-8.71L20,8.57a4.31,4.31,0,1,0-6.72.79,4.27,4.27,0,0,0,3,1.26A4.34,4.34,0,0,0,18.57,10l1.72,1.73a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,10.29ZM18,8a2.32,2.32,0,1,1,0-3.27A2.32,2.32,0,0,1,18,8Z"></path>
                </svg>
                <p className="text-sm text-gray-600">Search for icons</p>
                <p className="text-xs text-gray-500 mt-1">Start typing to find icons</p>
              </div>
            ) : (
              <ul
                ref={containerRef}
                className="grid gap-3 pt-1 h-full overflow-y-auto px-1 justify-center"
                style={{ gridTemplateColumns: "repeat(2, 94px)" }}
              >
                {icons.map((icon) => (
                  <li
                    key={icon.id}
                    className="rounded-[8px] bg-slate-100 overflow-hidden w-[94px] h-[94px] hover:shadow-[0px_2px_10px_2px_rgba(0,0,0,_0.1)] transition-all duration-300"
                  >
                    <Icon
                      editor={editor}
                      icon={icon}
                      onIconSelected={searchOnEnter ? handleIconSelected : undefined}
                      saveToBrandKit={fromBrandKit ? handleSaveToBrandKit : undefined}
                    />
                  </li>
                ))}
                {isFetching && (
                  <li className="col-span-2 flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const Icons = React.memo(IconsComponent);
